import {TextDisplayField} from '@app/resource-explorer/resource.form.config';
import {LabelResponse, ProjectFileCategoryID, ProjectResponse} from '@backend';
import {CustomFieldTypes, FormlyFieldTypes, HTMLInputFieldTypes} from '@models/form-field';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {ServiceInstanceMap} from '@services/landing-service-broker/service.map';
import {DatasetAggregationLevel, DatasetAggregationLevelOptions} from '@shared/labels/dataset-aggregation-level';
import {IdentifierHipaa, IdentifierHipaaOptions} from '@shared/labels/identifier-hipaa';
import {OtherSensitiveData, OtherSensitiveDataOptions} from '@shared/labels/other-sensitive-data';
import {PublicLicenseOptions} from '@shared/labels/public-license';
import {map, of} from 'rxjs';

export function buildDatasetDetailsFields(
  primaryParentProject: ProjectResponse,
  backend: ServiceInstanceMap,
): {
  fields: FormlyFieldConfig[][];
} {
  const hsd_identifiers = Object.keys(IdentifierHipaaOptions).filter(
    key => (<LabelResponse>IdentifierHipaaOptions[key]).sensitivity_level_id === 'hsd',
  );
  const isHSD = (field: FormlyFieldConfig): boolean =>
    (field.model?.__has_hipaa ?? false) &&
    (field.model?.identifiers_hipaa ?? []).some(id => hsd_identifiers.includes(id));
  const irbs = backend.UsersService.getUserIRBProtocols('me');
  const projectDocuments = primaryParentProject.project_file_versions
    .filter(doc => doc.is_latest_and_not_deleted)
    .map(doc => ({
      category: <ProjectFileCategoryID>doc.category,
      value: doc.file_version.id,
      label: `${doc.file_version.file_name} (${doc.file_version.time_created})`,
    }));
  const orgName = primaryParentProject.storage_organization.full_name ?? primaryParentProject.storage_organization.name;
  const osdOptions = <(LabelResponse & {disabled: boolean})[]>(
    Object.keys(OtherSensitiveDataOptions).map(id => ({...OtherSensitiveDataOptions[id], disabled: false}))
  );
  return {
    fields: [
      [
        // *-*-* OVERVIEW *-*-*
        {
          key: 'name',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'Dataset Title',
            required: true,
            type: HTMLInputFieldTypes.TEXT,
          },
        },
        {
          type: TextDisplayField,
          props: {
            label: 'Storage Organization',
            value: () => orgName,
          },
        },
        {
          key: 'show_storage_organization_logo',
          type: FormlyFieldTypes.CHECKBOX,
          defaultValue: true,
          props: {
            label: 'Show Storage Organization Logo',
          },
          wrappers: ['checkbox-wrapper'],
        },
        //TODO: Associated Projects (and Primary Associated Project)
        //TODO: Original Datasets
        {
          key: 'link_to_external_dataset',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'Link to External Dataset File',
            type: HTMLInputFieldTypes.TEXT,
          },
          validators: {validation: ['https-only']},
          expressions: {
            hide: field => field.model?.dataset_type == 'REDCap',
          },
        },
        {
          key: 'doi',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'Digital Object Identifier (DOI)',
            type: HTMLInputFieldTypes.TEXT,
          },
          validators: {validation: ['doi']},
          expressions: {
            hide: field => field.model?.dataset_type == 'REDCap',
          },
        },
        {
          key: 'redcap_project_url',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'REDCap Project URL',
            type: HTMLInputFieldTypes.TEXT,
            hint: 'Must start with https://',
          },
          validators: {validation: ['https-only']},
          expressions: {
            'props.required': field => field.model?.dataset_type == 'REDCap',
            hide: field => field.model?.dataset_type != 'REDCap',
          },
        },
        {
          key: '__redcap_extract_options',
          type: FormlyFieldTypes.SELECT,
          className: 'wide-form-field',
          props: {
            label: 'Extract from REDCap',
            options: [
              {label: 'None', value: 'none'},
              {label: 'Data Dictionary', value: 'data_dictionary'},
              {label: 'Single Report', value: 'redcap_report'},
              {label: 'All Data', value: 'extract_all'},
            ],
          },
          expressions: {
            'props.required': field => field.model?.dataset_type == 'REDCap',
            hide: field => field.model?.dataset_type != 'REDCap',
          },
        },
        {
          key: 'redcap_project_token',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'REDCap Project Token',
            type: HTMLInputFieldTypes.PASSWORD,
            hint: '32 letters.',
          },
          expressions: {
            'props.required': field =>
              field.model?.dataset_type == 'REDCap' && field.model?.__redcap_extract_options != 'none',
            hide: field => field.model?.dataset_type != 'REDCap',
          },
        },
        {
          key: 'redcap_report_id',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'REDCap Report ID',
            type: HTMLInputFieldTypes.TEXT,
          },
          expressions: {
            'props.required': field =>
              field.model?.dataset_type == 'REDCap' && field.model?.__redcap_extract_options == 'redcap_report',
            hide: field =>
              field.model?.dataset_type != 'REDCap' || field.model?.__redcap_extract_options != 'redcap_report',
          },
        },
      ],
      [
        // *-*-* DATA CONTENTS *-*-*
        {
          key: 'description',
          type: CustomFieldTypes.RICH_TEXT,
          props: {label: 'Description', required: true},
        },
        {
          key: 'keywords',
          type: CustomFieldTypes.CHIP_LIST,
          className: 'chip-list-field',
          props: {label: 'Keywords', required: true},
        },
        {
          key: 'variables_measured',
          type: CustomFieldTypes.CHIP_LIST,
          className: 'chip-list-field',
          props: {label: 'Variables Measured'},
          expressions: {
            hide: field => field.model?.dataset_type != 'Generic',
          },
        },
        {
          key: 'bids_structure',
          type: FormlyFieldTypes.CHECKBOX,
          props: {
            label: 'Brain Imaging Data Structure (BIDS)',
          },
          wrappers: ['checkbox-wrapper'],
          expressions: {hide: field => field.model?.dataset_type != 'DICOM'},
        },
        {
          key: 'quality',
          type: FormlyFieldTypes.SELECT,
          className: 'auto-width-field',
          props: {
            label: 'Quality',
            options: [
              {label: 'Unknown', value: 'unknown'},
              {label: 'Good', value: 'good'},
              {label: 'Medium', value: 'medium'},
              {label: 'Poor', value: 'poor'},
            ],
          },
          expressions: {
            'props.required': field => field.model?.dataset_type == 'DICOM',
            hide: field => field.model?.dataset_type != 'DICOM',
          },
        },
      ],
      [
        // *-*-* REGULATORY *-*-*
        {
          key: 'contract_id',
          type: FormlyFieldTypes.SELECT,
          className: 'wide-form-field',
          props: {
            label: 'Associated Contract',
            options: projectDocuments.filter(doc => doc.category == ProjectFileCategoryID.CONTRACT),
          },
        },
        {
          key: 'license_id',
          type: FormlyFieldTypes.SELECT,
          className: 'auto-width-field',
          props: {
            label: 'License',
            options: <LabelResponse[]>Object.keys(PublicLicenseOptions).map(id => PublicLicenseOptions[id]),
            labelProp: 'text',
            valueProp: 'id',
          },
        },
        {
          key: 'attribution',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'Attribution',
            hintStart: 'If the license allows reuse, required disclosures to do so.',
            type: HTMLInputFieldTypes.TEXT,
          },
        },
        {
          key: '__has_hipaa',
          type: FormlyFieldTypes.RADIO,
          className: 'wide-form-field',
          props: {
            label: 'Patient-Derived Data (HIPAA-Regulated)',
            required: true,
            options: [
              {label: 'Yes', value: true},
              {label: 'No', value: false},
            ],
          },
          hooks: {
            onInit: (field: FormlyFieldConfig) => {
              field.formControl.valueChanges.subscribe(() => {
                if (field.model?.__has_hipaa) {
                  field.model.identifiers_hipaa = (field.model.identifiers_hipaa ?? []).filter(
                    opt => opt != IdentifierHipaa.NA,
                  );
                  if (
                    field.model.data_aggregation_id == DatasetAggregationLevel.NONE ||
                    field.model.data_aggregation_id == DatasetAggregationLevel.NOT_SELECTED
                  )
                    field.model.data_aggregation_id = undefined;
                }
              });
              return of(true);
            },
          },
        },
        {
          expressions: {hide: field => field.model.__has_hipaa !== true},
          fieldGroup: [
            {
              key: 'identifiers_hipaa',
              type: CustomFieldTypes.HIPAA,
              className: 'wide-form-field',
              props: {
                label: 'HIPAA Identifiers',
                type: 'array',
              },
              expressions: {
                'props.required': field => field.model.__has_hipaa === true,
              },
            },
            {
              key: 'data_aggregation_id',
              type: FormlyFieldTypes.SELECT,
              className: 'wide-form-field',
              props: {
                label: 'Data Granularity',
                options: (<LabelResponse[]>(
                  Object.keys(DatasetAggregationLevelOptions).map(id => DatasetAggregationLevelOptions[id])
                )).filter(
                  label => label.id != DatasetAggregationLevel.NONE && label.id != DatasetAggregationLevel.NOT_SELECTED,
                ),
                labelProp: 'text',
                valueProp: 'id',
              },
              expressions: {'props.required': field => field.model.__has_hipaa === true},
            },
            {
              key: 'study_irb_number',
              type: FormlyFieldTypes.SELECT,
              className: 'wide-form-field',
              props: {
                label: 'IRB Number',
                options: irbs.pipe(map(irbs => irbs.map(irb => ({value: irb.id, label: `${irb.title} (#${irb.id})`})))),
              },
              expressions: {
                'props.required': isHSD,
              },
            },
            {
              key: 'dsp_id',
              type: FormlyFieldTypes.SELECT,
              className: 'wide-form-field',
              props: {
                label: 'Associated Data Security Plan',
                options: projectDocuments.filter(doc => doc.category == ProjectFileCategoryID.DATA_SECURITY_PLAN),
              },
              expressions: {
                'props.required': isHSD,
              },
            },
          ],
        },
        {
          key: 'other_sensitive_data',
          type: FormlyFieldTypes.MULTICHECKBOX,
          className: 'wide-form-field',
          props: {
            label: 'Other Sensitive, Highly Sensitive, or Controlled Data',
            required: true,
            options: osdOptions,
            labelProp: 'text',
            valueProp: 'id',
            type: 'array',
          },
          hooks: {
            onInit: (field: FormlyFieldConfig) => {
              function updateCheckboxes() {
                for (let option of osdOptions) {
                  option.disabled = false;
                  if (
                    option.id === OtherSensitiveData.NONE &&
                    field.model?.other_sensitive_data?.some(selection => selection !== OtherSensitiveData.NONE)
                  )
                    option.disabled = true;
                  if (
                    option.id !== OtherSensitiveData.NONE &&
                    field.model?.other_sensitive_data?.includes(OtherSensitiveData.NONE)
                  )
                    option.disabled = true;
                }
                field.props.options = [...osdOptions];
              }
              field.formControl.valueChanges.subscribe(() => updateCheckboxes());
              updateCheckboxes();
              return of(true);
            },
          },
        },
      ],
    ],
  };
}
